import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  LayoutDashboard, Users, GitBranch, ClipboardList, LifeBuoy, CalendarClock, CalendarDays,
  CheckSquare, Phone, Boxes, ScanText, FileSignature, Wrench, BarChart3, UserPlus,
  Inbox as InboxIcon, BellRing, ShoppingCart, Map as MapIcon, MapPin, Tags, Settings as SettingsIcon,
  FolderKanban, GanttChart, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ITEMS = [
  // Primary
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "nav.dashboard", group: "Primary" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, perm: "nav.calendar", group: "Primary" },
  { to: "/inbox", label: "Inbox (daily briefs)", icon: InboxIcon, perm: "nav.dashboard", group: "Primary" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, perm: "nav.tasks", group: "Primary" },
  { to: "/customers", label: "Customers", icon: Users, perm: "nav.customers", group: "Primary" },
  { to: "/leads", label: "Leads", icon: GitBranch, perm: "nav.leads", group: "Primary" },
  { to: "/jobcards", label: "Job Cards", icon: ClipboardList, perm: "nav.jobcards", group: "Primary" },
  { to: "/projects", label: "Projects", icon: FolderKanban, perm: "nav.jobcards", group: "Primary" },
  { to: "/timeline", label: "Timeline (Gantt)", icon: GanttChart, perm: "nav.jobcards", group: "Primary" },
  { to: "/team", label: "Team", icon: Users, perm: "nav.dashboard", group: "Primary" },
  { to: "/me", label: "My profile", icon: UserPlus, perm: null, group: "Primary" },
  { to: "/tickets", label: "Support Tickets", icon: LifeBuoy, perm: "nav.tickets", group: "Primary" },
  // Workflow
  { to: "/calls", label: "Call log", icon: Phone, perm: "nav.calls", group: "Workflow" },
  { to: "/followups", label: "Follow-ups", icon: BellRing, perm: "nav.leads", group: "Workflow" },
  { to: "/scheduler", label: "Service schedule", icon: CalendarClock, perm: "nav.schedule", group: "Workflow" },
  { to: "/order-requests", label: "Order requests", icon: ShoppingCart, perm: "nav.orders", group: "Workflow" },
  { to: "/equipment", label: "Equipment registry", icon: Wrench, perm: "nav.equipment", group: "Workflow" },
  { to: "/map", label: "Map of sites", icon: MapIcon, perm: "nav.customers", group: "Workflow" },
  // Tools
  { to: "/tools/onboarding?new=prospect", label: "Lead Onboarding", icon: UserPlus, perm: "tools.onboarding", group: "Tools" },
  { to: "/account-applications", label: "Account Application", icon: FileSignature, perm: "action.manage_account_applications", group: "Tools" },
  { to: "/trips", label: "Plan a trip", icon: MapPin, perm: "nav.leads", group: "Tools" },
  { to: "/tools/analytics", label: "Analytics hub", icon: BarChart3, perm: "tools.analytics", group: "Tools" },
  { to: "/procurement", label: "Procurement", icon: Boxes, perm: "nav.tools", group: "Tools" },
  { to: "/ocr/invoices", label: "Invoice OCR", icon: ScanText, perm: "tools.ocr", group: "Tools" },
  { to: "/tools/inventory-categories", label: "Inventory categories", icon: Tags, perm: "tools.inventory_categories", group: "Tools" },
  { to: "/tools/inventory-enrichment", label: "Inventory enrichment", icon: Sparkles, perm: "tools.inventory_categories", group: "Tools" },
  { to: "/tools/users-roles", label: "Users & Roles", icon: Users, perm: "tools.users_manager", group: "Tools" },
  // Account
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "nav.dashboard", group: "Account" },
];

export default function MorePage() {
  const { hasPermission, user } = useAuth();
  const visible = ITEMS.filter((i) => i.perm === null || hasPermission(i.perm));
  const groups = {};
  for (const i of visible) {
    if (!groups[i.group]) groups[i.group] = [];
    groups[i.group].push(i);
  }

  // A.7.2 — enrichment pending badge. Only fetched for owner/admin/manager.
  const [enrichPending, setEnrichPending] = useState(null);
  useEffect(() => {
    const role = (user?.role || "").toLowerCase();
    if (!["owner", "admin", "manager"].includes(role)) return;
    let cancelled = false;
    api.get("/inventory/enrichment/summary")
      .then(({ data }) => {
        if (!cancelled) setEnrichPending(data?.pending ?? null);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [user?.role]);

  return (
    <div className="hv-page-padding pb-12" data-testid="more-page">
      <div className="mb-5">
        <h1 className="font-display text-2xl md:text-4xl font-bold leading-none">All destinations</h1>
        <div className="text-[11px] text-[#647087] mt-1.5">
          Every page you can reach. Customise your sidebar in{" "}
          <Link to="/settings#menu-customisation" className="text-[#1FB6C1] underline">Settings → Menu</Link>.
        </div>
      </div>
      <div className="space-y-6">
        {Object.entries(groups).map(([gname, items]) => (
          <section key={gname} data-testid={`more-group-${gname.toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-2">{gname}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {items.map((it) => {
                const showBadge =
                  it.to === "/tools/inventory-enrichment"
                  && enrichPending !== null
                  && enrichPending > 0;
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    className="hv-card p-3 hover:border-[#1FB6C1]/40 hover:bg-[#1FB6C1]/[0.04] transition flex items-center gap-3"
                    data-testid={`more-item-${it.to.replace(/\W+/g, "-")}`}
                  >
                    <div className="w-9 h-9 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-[#1FB6C1] shrink-0">
                      <it.icon className="w-4 h-4" />
                    </div>
                    <div className="text-sm font-medium truncate flex-1">{it.label}</div>
                    {showBadge && (
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-100 border border-amber-400/40 shrink-0"
                        data-testid="more-enrichment-pending-badge"
                        title={`${enrichPending.toLocaleString()} rows pending review`}
                      >
                        {enrichPending > 999 ? "999+" : enrichPending}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
